import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { buildWhatsAppLink, WHATSAPP_NUMBER, BRAND_EMAIL, BRAND_ADDRESS, SOCIAL, GOOGLE_MAPS_EMBED, GOOGLE_MAPS_LINK, VISIT_HOURS } from '@/lib/api';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, ArrowUpRight } from 'lucide-react';

export default function Contact() {
  return (
    <div className="pt-32 pb-24 px-6 sm:px-10 max-w-[1600px] mx-auto" data-testid="contact-page">
      <p className="eyebrow mb-4">Visit / Enquire</p>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="font-serif-display text-6xl sm:text-8xl lg:text-9xl tracking-tighter leading-[0.85] max-w-4xl"
      >
        Come by. <em className="text-[color:var(--mb-primary)] not-italic">Namaste.</em>
      </motion.h1>

      <div className="mt-16 grid lg:grid-cols-12 gap-10">
        {/* Left column */}
        <div className="lg:col-span-5 space-y-10">
          <div className="flex items-start gap-4">
            <MapPin size={20} className="text-[color:var(--mb-primary)] mt-1 shrink-0" />
            <div>
              <p className="eyebrow mb-2">The Store</p>
              <p className="font-serif-display text-2xl sm:text-3xl leading-tight">
                Mahabir Bastralaya<br/>
                {BRAND_ADDRESS.line1}<br/>
                {BRAND_ADDRESS.line2}<br/>
                {BRAND_ADDRESS.line3}
              </p>
              <a href={GOOGLE_MAPS_LINK} target="_blank" rel="noreferrer" data-testid="contact-get-directions" className="mt-4 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.3em] uppercase text-[color:var(--mb-primary)] hover:underline">
                Get Directions <ArrowUpRight size={12} />
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <Phone size={20} className="text-[color:var(--mb-primary)] mt-1 shrink-0" />
            <div>
              <p className="eyebrow mb-2">WhatsApp / Call</p>
              <a href={buildWhatsAppLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="font-serif-display text-2xl sm:text-3xl hover:text-[color:var(--mb-primary)]" style={{ transition: 'color 200ms ease' }}>
                +91 {WHATSAPP_NUMBER.slice(2, 7)} {WHATSAPP_NUMBER.slice(7)}
              </a>
              <p className="mt-2 text-sm text-[color:var(--mb-text-muted)]">We reply within a few hours, always.</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <Mail size={20} className="text-[color:var(--mb-primary)] mt-1 shrink-0" />
            <div>
              <p className="eyebrow mb-2">Email</p>
              <a href={`mailto:${BRAND_EMAIL}`} data-testid="contact-email" className="font-serif-display text-2xl sm:text-3xl hover:text-[color:var(--mb-primary)] break-all" style={{ transition: 'color 200ms ease' }}>
                {BRAND_EMAIL}
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <Clock size={20} className="text-[color:var(--mb-primary)] mt-1 shrink-0" />
            <div>
              <p className="eyebrow mb-2">Visiting Hours</p>
              <p className="font-serif-display text-xl">{VISIT_HOURS.days}</p>
              <p className="mt-1">{VISIT_HOURS.time}</p>
              <p className="text-[color:var(--mb-text-muted)] mt-1">{VISIT_HOURS.closed}</p>
            </div>
          </div>

          <div className="pt-6 border-t border-[color:var(--mb-border)]">
            <p className="eyebrow mb-4">Follow the House</p>
            <div className="flex items-center gap-3">
              <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="w-11 h-11 flex items-center justify-center rounded-full border border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}>
                <Instagram size={18} />
              </a>
              <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="w-11 h-11 flex items-center justify-center rounded-full border border-[color:var(--mb-border)] hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)]" style={{ transition: 'border-color 200ms ease, color 200ms ease' }}>
                <Facebook size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Right column — Map + Enquiry */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          <div className="w-full aspect-[16/10] overflow-hidden border border-[color:var(--mb-border)] bg-[color:var(--mb-surface)]" data-testid="google-map">
            <iframe
              title="Mahabir Bastralaya Location"
              src={GOOGLE_MAPS_EMBED}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="border border-[color:var(--mb-border)] p-8 sm:p-10 bg-[color:var(--mb-surface)]">
            <p className="eyebrow mb-4">Enquiry</p>
            <h3 className="font-serif-display text-3xl sm:text-4xl leading-tight mb-4">Have a piece in mind?</h3>
            <p className="text-[color:var(--mb-text-muted)] leading-relaxed mb-6 font-light">
              Whether it's a Tibetan Bakhu, a Bhutanese kira, or a made-to-order community dress — WhatsApp us with your preferences. We'll respond with availability, pricing and lead time.
            </p>
            <a href={buildWhatsAppLink()} target="_blank" rel="noreferrer" data-testid="contact-cta-whatsapp" className="btn-red">
              Start on WhatsApp
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
